/**
 * Connect-dialog guide column: short summary, toggleable numbered steps,
 * unlock line, and live handshake completeness.
 */

import { el } from '../../core/dom.js';
import { PROVIDER_GUIDES, CRED_UNLOCKS, credFindCopy } from './guides.js';
import { SCHEMAS } from './schemas.js';

const DOMAIN_RE = /\{([\w.-]+\.[a-z]{2,}(?:\/[^\s{}]*)?)\}/gi;

/**
 * Turn "{domain.com}" / "{domain.com/path}" markers into external links (plain <a href> only).
 * @param {string} step
 * @returns {(string|HTMLElement)[]}
 */
export function linkifyGuideStep(step) {
  const text = String(step || '');
  const parts = [];
  let last = 0;
  DOMAIN_RE.lastIndex = 0;
  let match;
  while ((match = DOMAIN_RE.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(el('a', {
      href: `https://${match[1]}`,
      target: '_blank',
      rel: 'noopener noreferrer',
    }, match[1]));
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : [text];
}

/**
 * One-line "What it unlocks" under the dialog title.
 * @param {string} slug
 * @returns {HTMLElement|null}
 */
export function unlockLine(slug) {
  const unlock = CRED_UNLOCKS[slug] || { lines: ['Live data from this provider in chat and Insights'] };
  const line = (unlock.lines || []).join(' · ');
  if (!line) return null;
  return el('p', { class: 'cx-unlock' }, el('span', { class: 'cx-kicker' }, 'What it unlocks'), ' ', line);
}

/**
 * "Where to find these" + optional step-by-step toggle.
 * @param {string} slug
 * @param {string} providerName
 * @returns {HTMLElement}
 */
export function renderGuideColumn(slug, providerName) {
  const guide = PROVIDER_GUIDES[slug] || null;
  const steps = guide
    ? el('ol', { class: 'cx-guide-steps' },
      guide.steps.map((step, index) => el('li', {},
        el('span', { class: 'cx-guide-num' }, String(index + 1)),
        el('span', {}, ...linkifyGuideStep(step)))))
    : null;
  const panel = steps
    ? el('div', { class: 'cx-guide-panel', hidden: true },
      el('h3', { class: 'cx-guide-title' }, guide.title),
      steps)
    : null;
  const toggle = guide
    ? el('button', {
      type: 'button',
      class: 'cx-guide-toggle',
      onClick: () => {
        // Hide the whole panel (title + steps) while closed.
        const open = panel.hasAttribute('hidden');
        if (open) panel.removeAttribute('hidden');
        else panel.setAttribute('hidden', '');
        toggle.textContent = open ? 'Hide step-by-step guide' : 'Open step-by-step guide';
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      },
      'aria-expanded': 'false',
    }, 'Open step-by-step guide')
    : null;
  return el('aside', { class: 'cx-guide' },
    el('div', { class: 'cx-guide-find' },
      el('p', { class: 'cx-kicker' }, 'Where to find these'),
      el('p', { class: 'cx-guide-copy' }, credFindCopy(guide, providerName)),
      toggle),
    panel);
}

function handshakeRow(state, label) {
  const cls = state === 'ok' ? 'is-ok' : state === 'fail' ? 'is-fail' : 'is-wait';
  const mark = state === 'ok' ? '✓' : state === 'fail' ? '!' : '';
  return el('div', { class: `cx-hs ${cls}` },
    el('span', { class: 'cx-hs-dot', 'aria-hidden': 'true' }, mark),
    el('span', {}, label));
}

/**
 * Live handshake panel (completeness + existing save-time API check copy).
 * @returns {{ root: HTMLElement, paint: Function, updateFromForm: Function }}
 */
export function createHandshake(slug) {
  const list = el('div', { class: 'cx-hs-list' });
  const root = el('div', { class: 'cx-handshake' },
    el('p', { class: 'cx-kicker' }, 'Live handshake'),
    list,
    el('p', { class: 'cx-hint' }, 'A real API call runs before anything is saved.'));

  function paint(parsed, reachable, sample, extra) {
    const more = extra || {};
    const reachLabel = more.latency_ms != null
      ? `Property reachable · ${more.latency_ms} ms`
      : 'Property reachable';
    const sampleLabel = more.tool_count != null
      ? `Sample query returned ${more.tool_count} tool${more.tool_count === 1 ? '' : 's'}`
      : (sample === 'ok' ? 'Sample query ready' : 'Sample query');
    list.replaceChildren(
      handshakeRow(parsed, 'Credential parsed'),
      handshakeRow(reachable, reachLabel),
      handshakeRow(sample, sampleLabel),
    );
  }

  function updateFromForm(rootForm) {
    const schema = SCHEMAS[slug];
    let parsed = 'wait';
    if (schema) {
      const filled = schema.fields
        .filter((field) => !field.optional)
        .every((field) => {
          const node = rootForm.querySelector(`[data-cred="${field.key}"]`);
          return node && String(node.value || '').trim();
        });
      parsed = filled ? 'ok' : 'wait';
    } else {
      const node = rootForm.querySelector('[data-cred="value"]');
      parsed = node && String(node.value || '').trim() ? 'ok' : 'wait';
    }
    paint(parsed, 'wait', 'wait');
  }

  paint('wait', 'wait', 'wait');
  return { root, paint, updateFromForm };
}
